import { useState } from 'react';
import { SERVER_URL, clearServerUrl, setServerUrl } from '../server.ts';

/**
 * Shown when no Rally server answers. This page is a client for any Rally
 * deployment — paste its URL, or deploy one with the button below. Same-origin
 * deployments (the usual self-hosted case) never see this screen.
 */
export function Connect({ detail }: { detail?: string }) {
  const [url, setUrl] = useState(SERVER_URL);
  const [state, setState] = useState<'idle' | 'checking'>('idle');

  const connect = async (candidate: string) => {
    const trimmed = candidate.trim().replace(/\/+$/, '');
    if (!trimmed) return;
    setState('checking');
    const res = await probeServerWith(trimmed);
    if (res.ok) {
      setServerUrl(trimmed);
      return;
    }
    setState('idle');
    alert(`That URL did not answer /api/health (${res.detail}). Check the URL and that the deployment allows this page's origin (ALLOWED_ORIGINS).`);
  };

  return (
    <div className="app">
      <nav className="top">
        <a className="brand" href="#/">
          <span className="logo" aria-hidden="true">●●</span> Rally <span className="brand-sub">for Alexa+</span>
        </a>
        <span className="tagline">Say it once. Rally gets the whole group to the same table.</span>
        <a className="gh" href="https://github.com/Hisernberg/amazon-hackathon-2026" target="_blank" rel="noreferrer">Source ↗</a>
      </nav>
      <main>
        <div className="connect">
          <h1>Connect to a Rally server</h1>
          <p className="connect-sub">
            This page is the simulated Alexa+ host. It drives a real Rally MCP server
            (Streamable HTTP) somewhere else. {SERVER_URL
              ? <>The configured server <code>{SERVER_URL}</code> did not answer{detail ? ` (${detail})` : ''}. Retry below, or point it somewhere else.</>
              : 'No server is configured for this page yet.'}
          </p>
          <div className="connect-row">
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://your-rally.vercel.app"
              spellCheck={false}
              aria-label="Rally server URL"
            />
            <button disabled={state === 'checking' || !url.trim()} onClick={() => connect(url)}>
              {state === 'checking' ? 'Checking…' : 'Connect'}
            </button>
          </div>
          <h2>Deploy your own in one click</h2>
          <p>
            <a href="https://vercel.com/new/clone?repository-url=https%3A%2F%2Fgithub.com%2FHisernberg%2Famazon-hackathon-2026&env=SUPABASE_URL,SUPABASE_SERVICE_ROLE_KEY,RALLY_SECRET&project-name=rally&repository-name=amazon-hackathon-2026" target="_blank" rel="noreferrer">
              <img src="https://vercel.com/button" alt="Deploy with Vercel" height="32" />
            </a>
          </p>
          <p className="connect-note">
            After deploying, set <code>ALLOWED_ORIGINS</code> on Vercel to this page's origin
            (<code>{window.location.origin}</code>) so the browser client may call <code>/mcp</code>, then paste the URL above.
          </p>
          <h2>Or run it locally</h2>
          <pre>pnpm install && pnpm start   # serves MCP + API on http://127.0.0.1:8000</pre>
          <p className="connect-note">
            Cross-origin browsers need <code>ALLOWED_ORIGINS</code> on the server, too.
          </p>
          {SERVER_URL && (
            <p>
              <button className="ghost small" onClick={clearServerUrl}>Forget this server</button>
            </p>
          )}
          <p className="connect-links">
            <a href="https://github.com/Hisernberg/amazon-hackathon-2026" target="_blank" rel="noreferrer">Source (Apache-2.0)</a>
            {' · '}
            <a href="https://huggingface.co/datasets/Nabidnur/rally-voice-evals" target="_blank" rel="noreferrer">Eval dataset</a>
            {' · '}
            <a href="https://huggingface.co/spaces/Nabidnur/rally-alexa" target="_blank" rel="noreferrer">This Space</a>
          </p>
        </div>
      </main>
    </div>
  );
}

async function probeServerWith(base: string, timeoutMs = 5000): Promise<{ ok: boolean; detail?: string }> {
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), timeoutMs);
    const res = await fetch(base + '/api/health', { signal: ctrl.signal });
    clearTimeout(timer);
    return res.ok ? { ok: true } : { ok: false, detail: `HTTP ${res.status}` };
  } catch (err) {
    return { ok: false, detail: (err as Error).name === 'AbortError' ? 'timed out' : 'unreachable' };
  }
}
